import Delete from '@monofront/design-system/visuals/Icons/Old/Delete';
import SadCart from '@monofront/design-system/visuals/Icons/Old/SadCart';
import Button from '@monofront/design-system/components/Button';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { trackShoppingCartUpdate } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import {
  IShoppingCartProduct,
  AddToShoppingCartSource,
} from '@monofront/types';
import { useState } from 'react';
import { ModalConfirmation } from '@components/ModalConfirmation';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import { formatSearchProductToTracking } from '@monofront/business/utils';
import useI18n from '@hooks/i18n/useI18n';
import { isMobileApp } from '@utils/environment';
import { EVENT_SOURCE } from '@monofront/business/constants';

interface IButtonRemoveProductsFromShoppingCartProps {
  products: IShoppingCartProduct[];
  shoppingCartId: string;
}

const ButtonRemoveProductsFromShoppingCart: React.FC<
  IButtonRemoveProductsFromShoppingCartProps
> = ({ products, shoppingCartId }) => {
  const [isEmptyModalOpen, setIsEmptyModalOpen] = useState(false);
  const { deleteProductsFromShoppingCart } = useShoppingCart();

  const tCart = useTranslations('Cart');
  const tCatalog = useTranslations('Catalog');
  const { currency } = useI18n();

  return (
    <>
      <div className="container">
        <Button
          variant="secondary"
          hasMinWidth={false}
          onClick={() => setIsEmptyModalOpen(true)}
          disabled={Boolean(products === undefined)}
          dataTestId="ButtonRemoveProductsFromShoppingCart-Button-empty-cart"
        >
          <Delete color="var(--black)" />
          {tCatalog('emptyBasketCTA')}
        </Button>
        {isEmptyModalOpen && (
          <ModalConfirmation
            message={tCart('emptyCartConfirmation')}
            confirmButtonLabel="OK"
            confirmButtonOnClick={async () => {
              deleteProductsFromShoppingCart(shoppingCartId, products);
              // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
              trackShoppingCartUpdate(
                'removeFromCart',
                products,
                undefined,
                undefined,
              );

              trackEvent({
                type: 'LFRemoveFromCart',
                data: {
                  eventSource: isMobileApp
                    ? EVENT_SOURCE.APP
                    : EVENT_SOURCE.WEB,
                  products: products.map((product) =>
                    formatSearchProductToTracking(product, product.quantity),
                  ),
                  context: { source: AddToShoppingCartSource.DEFAULT },
                  currency,
                  is_substitute: false,
                },
              });
              toast(tCart('cartEmptied'));
              setIsEmptyModalOpen(false);
            }}
            cancelButtonOnClick={() => setIsEmptyModalOpen(false)}
            isOpen={isEmptyModalOpen}
          >
            <SadCart />
          </ModalConfirmation>
        )}
      </div>
      <style jsx>{`
        .container {
          display: flex;
          justify-content: flex-start;
        }

        .container > :global(button) {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--font-size-2);
          max-width: 220px;
          padding-right: var(--space-6);
        }
      `}</style>
    </>
  );
};

export default ButtonRemoveProductsFromShoppingCart;
